// 상세 화면에서 의미 그룹 하나를 감싸는 카드 섹션.
// 공용 리스트 컴포넌트(#63)와 동일한 override slot 패턴(sx / className / action ReactNode)을 따른다.

import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { SxProps, Theme } from "@mui/material/styles";

export interface DetailSectionProps {
  title: string;
  description?: string;
  // 헤더 우측 영역(예: <RelatedLink>)
  action?: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
  // 루트 Paper에 병합되는 override slot
  sx?: SxProps<Theme>;
  className?: string;
}

export const DetailSection: React.FC<DetailSectionProps> = ({
  title,
  description,
  action,
  collapsible = false,
  defaultOpen = true,
  children,
  sx,
  className,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <Paper
      variant="outlined"
      className={className}
      sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, ...sx }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 0.5 }}
      >
        <Box>
          <Typography variant="subtitle1">{title}</Typography>
          {description && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {action}
          {collapsible && (
            <IconButton
              size="small"
              aria-label={open ? "섹션 접기" : "섹션 펼치기"}
              aria-expanded={open}
              onClick={() => setOpen((prev) => !prev)}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform .2s",
                }}
              />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {collapsible ? <Collapse in={open}>{children}</Collapse> : children}
    </Paper>
  );
};
